import {useCallback, useEffect, useRef} from 'react';

import {view} from '@sail/ui';

import {Conversation} from '@stripe-internal/conversations-client';
import {
  LoadingIndicator,
  LoadingTextProvider,
  TypingIndicatorNext,
  useConversationLogItems,
} from '@stripe-internal/conversations-ui';

import {MobileLogItems} from './MobileLogItems';

type Props = {
  conversation: Conversation;
  animated?: boolean;
  disableInteraction?: boolean;
};

const supportsReverseColumn = () =>
  window?.CSS?.supports &&
  typeof window.CSS.supports === 'function' &&
  window.CSS.supports('overflow-anchor: auto');

const SCROLL_DELAY_MS = 16;

const scrollToAnchor = (anchor?: HTMLDivElement | null) => {
  setTimeout(() => {
    requestAnimationFrame(() => {
      anchor?.scrollIntoView({behavior: 'smooth'});
    });
  }, SCROLL_DELAY_MS);
};

export const MobileLog = ({
  conversation,
  animated,
  disableInteraction,
}: Props) => {
  const items = useConversationLogItems(conversation);
  const logRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback((instant = false) => {
    if (supportsReverseColumn() && logRef.current) {
      logRef.current.scrollTop = 0;
    } else if (instant && anchorRef.current) {
      anchorRef.current.scrollIntoView({behavior: 'smooth', block: 'end'});
    } else {
      scrollToAnchor(anchorRef.current);
    }
  }, []);

  useEffect(() => {
    if (supportsReverseColumn()) {
      scrollToBottom();
    } else {
      // Only auto-scroll using the anchor if the browser does not support
      // column-reverse auto-scrolling.
      // This won't respect the user's scroll position if they've scrolled up
      scrollToAnchor(anchorRef.current);
    }
  }, [items, scrollToBottom]);

  // Handle resize events
  useEffect(() => {
    let timeoutId: number;

    const handleLayoutChange = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = window.setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              scrollToBottom(true);
            });
          });
        });
      }, 50); // delay to ensure keyboard animation has started
    };

    window.addEventListener('resize', handleLayoutChange);
    window.addEventListener('inputresize', handleLayoutChange);
    window.visualViewport?.addEventListener('resize', handleLayoutChange);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      window.removeEventListener('resize', handleLayoutChange);
      window.removeEventListener('inputresize', handleLayoutChange);
      window.visualViewport?.removeEventListener('resize', handleLayoutChange);
    };
  }, [scrollToBottom]);

  const handleScroll = () => {
    const logContainer = logRef.current;

    // Due to certain screen resolutions and zoom settings, the `scrollTop` field on the Log container
    // can sometimes get set to a value that's between 0 and 1, which breaks auto-scrolling.
    //
    // Since we're using `flex-direction: column-reverse` to enable auto-scrolling, this will only work
    // if `scrollTop` is set to 0. To fix this, we're setting it to 0 if `scrollTop` ever gets into a weird state.
    if (
      logContainer &&
      logContainer.scrollTop > 0 &&
      logContainer.scrollTop <= 1
    ) {
      logContainer.scrollTop = 0;
    }
  };

  // default is column-reverse, so rendering these in reverse order by default as well
  const logComponents = [];

  logComponents.push(
    <view.div
      key="anchor"
      ref={anchorRef}
      css={{
        flexGrow: '1',
        flexShrink: '1',
      }}
    />,
  );

  logComponents.push(
    <LoadingIndicator key="loading-indicator" conversation={conversation} />,
  );

  logComponents.push(
    <TypingIndicatorNext
      key="typing-indicator"
      conversation={conversation}
      animated={animated}
    />,
  );

  logComponents.push(
    <MobileLogItems
      key="log-items"
      ref={contentRef}
      conversation={conversation}
      items={items}
      animated={animated}
      disableInteraction={disableInteraction}
    />,
  );

  // column-reverse gives us magical auto-scrolling
  let flexDirection: 'column-reverse' | 'column' = 'column-reverse';
  let children = logComponents;
  if (!supportsReverseColumn()) {
    flexDirection = 'column';
    children = [...logComponents].reverse();
  }

  return (
    <LoadingTextProvider>
      <view.div css={{flexGrow: '1', position: 'relative', height: '100%'}}>
        <view.div
          ref={logRef}
          onScroll={handleScroll}
          css={{
            display: 'flex',
            flexDirection,
            overflowY: 'auto',
            overflowX: 'hidden',
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: 0,
            left: 0,
            padding: 'medium',
            paddingBottom: 'medium',
            // eslint-disable-next-line no-nested-ternary
            gap: 'large',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {children}
        </view.div>
      </view.div>
    </LoadingTextProvider>
  );
};
